import { Show } from '@/components/show'
import type { Kpi } from '@/types/kpi/kpi'
import { useNavigate } from 'react-router-dom'

interface KpiCardProps {
  cardTitle: string
  data: Kpi | undefined
  redirectTo: string | undefined
  orangeLabel: string
  greenLabel: string
  redLabel: string
  isLoading: boolean
}

export const KpiCard = ({
  cardTitle,
  data,
  redirectTo,
  greenLabel,
  orangeLabel,
  redLabel,
  isLoading,
}: KpiCardProps) => {
  const navigate = useNavigate()

  function navigateToCardPage() {
    if (redirectTo) {
      navigate(`/${redirectTo}`)
    }
  }

  function showValue(value: number | undefined) {
    if (value) {
      const padStart = value === 0 ? 1 : 2
      return String(value).padStart(padStart, '0')
    }

    return 0
  }

  function generateLabelSkeleton(value: string) {
    return (
      <div className="bg-ring/20 text-transparent rounded-md animate-pulse">
        {value}
      </div>
    )
  }

  function generateSkeleton() {
    return (
      <span className="text-transparent bg-ring/20 rounded-md animate-pulse">
        00
      </span>
    )
  }

  return (
    <button
      onClick={navigateToCardPage}
      className="w-full cursor-pointer border border-[var(--brand-blue-2)] rounded-2xl py-6 px-8 shadow-md"
    >
      <div className="flex justify-start text-base text-[var(--brand-blue-2)] font-bold">
        <Show when={isLoading}>{generateLabelSkeleton(cardTitle)}</Show>
        <Show when={!isLoading}>{cardTitle}</Show>
      </div>
      <div className="flex gap-10 mt-5">
        <div className="font-bold">
          <div className="mb-3">
            <Show when={isLoading}>{generateLabelSkeleton('Totais')}</Show>
            <Show when={!isLoading}>Totais</Show>
          </div>
          <span className="text-3xl">
            <Show when={isLoading}>{generateSkeleton()}</Show>
            <Show when={!isLoading}>{showValue(data?.total)}</Show>
          </span>
        </div>
        <div className="w-1 bg-border shrink-1"></div>
        <div className="text-[var(--brand-red)]">
          <div className="text-xs font-bold mb-4">
            <Show when={isLoading}>{generateLabelSkeleton(redLabel)}</Show>
            <Show when={!isLoading}>{redLabel}</Show>
          </div>
          <span className="text-2xl font-bold">
            <Show when={isLoading}>{generateSkeleton()}</Show>
            <Show when={!isLoading}>{showValue(data?.overdue)}</Show>
          </span>
        </div>
        <div className="text-[var(--brand-orange)]">
          <div className="text-xs font-bold mb-4">
            <Show when={isLoading}>{generateLabelSkeleton(orangeLabel)}</Show>
            <Show when={!isLoading}>{orangeLabel}</Show>
          </div>
          <span className="text-2xl font-bold">
            <Show when={isLoading}>{generateSkeleton()}</Show>
            <Show when={!isLoading}>{showValue(data?.due)}</Show>
          </span>
        </div>
        <div className="text-[var(--brand-green)]">
          <div className="text-xs font-bold mb-4">
            <Show when={isLoading}>{generateLabelSkeleton(greenLabel)}</Show>
            <Show when={!isLoading}>{greenLabel}</Show>
          </div>
          <span className="text-2xl font-bold">
            <Show when={isLoading}>{generateSkeleton()}</Show>
            <Show when={!isLoading}>{showValue(data?.upToDate)}</Show>
          </span>
        </div>
      </div>
    </button>
  )
}
